import { BottomSheet } from '@/components/BottomSheet';
import { ChevronDownIcon, SearchIcon } from '@/components/icons';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { Domain, DomainSelectItem } from '@/services/inventory-api.service';
import { inventoryApi } from '@/services/inventory-api.service';
import { classNames } from '@/utils/classNames';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './SkiAreaDropdown.module.css';

// const getCountries = (domain: Domain): string => {
//  if (!domain.countries || domain.countries.length === 0) return '';
//  return domain.countries.map((c) => c.title).join(', ');
//};

const formatDomainName = (domain: DomainSelectItem): string => domain.value ?? '';

type SkiAreaDropdownProps = {
  value: Domain | null;
  onChange: (domain: DomainSelectItem) => void;
};

export const SkiAreaDropdown = ({ value, onChange }: SkiAreaDropdownProps) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [domains, setDomains] = useState<DomainSelectItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadDomains = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await inventoryApi.getDomains();
      setDomains(data);
    } catch (err) {
      setError('Failed to load ski areas');
      console.error('Failed to load domains:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !domains.length && !isLoading && !error) {
      void loadDomains();
    }
  }, [isOpen, domains.length, isLoading, error, loadDomains]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen]);

  const handleTriggerClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleDomainSelect = useCallback(
    (domain: DomainSelectItem) => {
      onChange(domain);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const filteredDomains = domains.filter((domain) => {
    const searchLower = searchQuery.toLowerCase();
    const label = (domain.value ?? '').toLowerCase();
    return label.includes(searchLower);
  });

  const searchAndListContent = (
    <>
      <div className={styles.searchContainer}>
        <SearchIcon className={styles.searchIcon} />
        <input
          ref={searchInputRef}
          type="text"
          className={styles.searchInput}
          placeholder="Search ski areas..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      <div className={styles.resortsList}>
        {isLoading && (
          <div className={styles.emptyState}>
            <p>Loading ski areas...</p>
          </div>
        )}

        {error && (
          <div className={styles.emptyState}>
            <p className={styles.errorText}>{error}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={() => {
                void loadDomains();
              }}
            >
              Try again
            </button>
          </div>
        )}

        {!isLoading && !error && filteredDomains.length === 0 && searchQuery && (
          <div className={styles.emptyState}>
            <p>No ski areas found</p>
            <p className={styles.emptyStateSubtext}>Try adjusting your search</p>
          </div>
        )}

        {!isLoading && !error && filteredDomains.length === 0 && !searchQuery && (
          <div className={styles.emptyState}>
            <p>No ski areas available</p>
          </div>
        )}

        {!isLoading && !error && filteredDomains.length > 0 && (
          <>
            {filteredDomains.map((domain) => (
              <button
                key={domain.id}
                type="button"
                className={classNames(
                  styles.resortItem,
                  value?.pk === domain.id && styles.selected
                )}
                onClick={() => {
                  handleDomainSelect(domain);
                }}
              >
                <span className={styles.resortName}>{formatDomainName(domain)}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </>
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        Change
        <ChevronDownIcon className={classNames(styles.chevron, isOpen && styles.rotated)} />
      </button>

      {isOpen &&
        (isMobile ? (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => {
              setSearchQuery('');
              setIsOpen(false);
            }}
            title="Ski area"
          >
            {searchAndListContent}
          </BottomSheet>
        ) : (
          <div className={styles.dropdown}>{searchAndListContent}</div>
        ))}
    </div>
  );
};
